import { useState, useRef, useEffect } from "react";
import { Chakra } from "@airhelp/react-ui-components";
import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";

import { trackEvents } from "utils/trackingUtils";

import { useTranslations } from "contexts/LocalizationContext/hooks";
import { useUserContext } from "contexts/HelpAFriend/UserContext";

import { MODAL_COPY_LINK_GTM, MODAL_COPY_LINK_MP } from "modules/help-a-friend/trackingConsts";

const { Heading, Text, Input, Button, InputGroup, InputRightElement } = Chakra;

const copied = keyframes`
  0% {
    transform: scale(0.2);
    opacity: 0;
  }

  33% {
    transform: scale(1);
    opacity: 1;
  }

  100% {
    transform: scale(1.5);
    opacity: 0;
  }
`;

const CopiedText = styled.span`
  animation: ${copied} 1100ms linear;
`;

const ReturningUserView = () => {
  const translations = useTranslations();
  const { updateShareLink, updateEmail, userEmail, shareLink } = useUserContext();
  const [textCopied, setTextCopied] = useState(false);
  const [copiedTimeout, setCopiedTimeout] = useState(null);

  const shareLinkRef = useRef(null);

  const handleCopyClick = (e) => {
    if (!textCopied) {
      shareLinkRef.current.select();
      document.execCommand("copy");
      e.currentTarget.focus();
      setTextCopied(true);

      setCopiedTimeout(setTimeout(() => setTextCopied(false), 1100));
      trackEvents({ gtmEvent: MODAL_COPY_LINK_GTM, mixpanelEvent: MODAL_COPY_LINK_MP });
    }
  };

  const handleEmailReset = () => {
    updateEmail("");
    updateShareLink("");
  };

  useEffect(
    () => () => {
      clearTimeout(copiedTimeout);
    },
    [copiedTimeout]
  );

  return (
    <>
      <Heading variant="h3" as="h3" mb={3}>
        {translations("modal_spread_word")}
      </Heading>
      <Text mb={10}>{translations("modal_share_link_with_anyone")}</Text>
      <InputGroup mb={6} size="md">
        <Input
          value={shareLink}
          isReadOnly
          textOverflow="ellipsis"
          color="greyscale.500"
          focusBorderColor="greyscale.500"
          pr={24}
          ref={shareLinkRef}
        />
        <InputRightElement ml={4} width="80px">
          <Button
            variant="ghost"
            colorScheme="primary"
            fontWeight="semibold"
            whiteSpace="initial"
            size="s"
            _focus={{ boxShadow: "none" }}
            onClick={handleCopyClick}
          >
            {textCopied ? (
              <CopiedText>{translations("modal_copied")}</CopiedText>
            ) : (
              <span>{translations("modal_copy_link")}</span>
            )}
          </Button>
        </InputRightElement>
      </InputGroup>
      <Text
        data-testid="modal-info-text"
        fontSize="sm"
        dangerouslySetInnerHTML={{
          __html: translations("modal_link_generated_for", { "%email%": userEmail }),
        }}
      />
      <Text fontSize="sm" display="inline" verticalAlign="middle">
        {translations("modal_doesnt_look_right")}
      </Text>
      <Text
        as="button"
        textDecoration="underline"
        onClick={handleEmailReset}
        ml={1}
        color="inherit"
        fontSize="sm"
        display="inline"
      >
        {translations("modal_enter_email_again")}
      </Text>
    </>
  );
};

export default ReturningUserView;
